import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { CircleCheck, Send } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { BUSINESS } from "../lib/data";
import logoMark from "../assets/releaf_mark.png";

function Field({ field, value, onChange }) {
  const base = "mt-2 w-full rounded-md border border-linen bg-ivory px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-gold";
  const label = (
    <label htmlFor={`field-${field.id}`} className="text-xs font-semibold uppercase tracking-[0.2em] text-moss">
      {field.label} {field.required && <span className="text-gold-dark">*</span>}
    </label>
  );
  if (field.type === "checkbox") {
    return (
      <label className="flex cursor-pointer items-start gap-3 rounded-md border border-linen bg-ivory p-4 text-sm leading-relaxed text-forest">
        <input
          id={`field-${field.id}`}
          data-testid={`form-field-${field.id}`}
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 accent-[#1A3626]"
        />
        <span>{field.label} {field.required && <span className="text-gold-dark">*</span>}</span>
      </label>
    );
  }
  if (field.type === "textarea") {
    return (
      <div>
        {label}
        <textarea id={`field-${field.id}`} data-testid={`form-field-${field.id}`} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} />
      </div>
    );
  }
  if (field.type === "select") {
    return (
      <div>
        {label}
        <select id={`field-${field.id}`} data-testid={`form-field-${field.id}`} value={value || ""} onChange={(e) => onChange(e.target.value)} className={base}>
          <option value="">Select an option...</option>
          {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  return (
    <div>
      {label}
      <input id={`field-${field.id}`} data-testid={`form-field-${field.id}`} type={field.type} value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} />
    </div>
  );
}

export default function FormPage() {
  const { formType } = useParams();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("booking") || "";
  const [definition, setDefinition] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [answers, setAnswers] = useState({});
  const [signature, setSignature] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/forms/${formType}`).then((res) => setDefinition(res.data)).catch(() => setNotFound(true));
  }, [formType]);

  const requiredMissing =
    definition &&
    (contact.name.trim().length < 2 ||
      !/^\S+@\S+\.\S+$/.test(contact.email.trim()) ||
      signature.trim().length < 2 ||
      !agreed ||
      definition.fields.some((f) => {
        if (!f.required) return false;
        const v = answers[f.id];
        return f.type === "checkbox" ? !v : !String(v || "").trim();
      }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/forms/${formType}/submit`, {
        booking_id: bookingId || null,
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
        answers,
        signature,
        agreed,
      });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="form-page" className="min-h-screen bg-cream px-5 pb-24 pt-32 sm:px-8">
      <div className="mx-auto max-w-2xl">
        <Link to="/" className="flex items-center gap-3" data-testid="form-page-logo">
          <img src={logoMark} alt="Releaf monogram" className="h-11 w-auto" />
          <span>
            <span className="block font-serif text-lg text-forest">Releaf</span>
            <span className="block text-[8px] font-semibold uppercase tracking-[0.3em] text-gold-dark">Wellness &amp; Aesthetics</span>
          </span>
        </Link>

        {notFound && (
          <div data-testid="form-not-found" className="mt-10 rounded-lg border border-linen bg-ivory p-10 text-center">
            <h1 className="font-serif text-3xl text-forest">Form not found</h1>
            <p className="mt-3 text-sm text-moss">Please check your link, or call us at {BUSINESS.phone}.</p>
          </div>
        )}

        {!notFound && !definition && <p className="mt-10 text-sm text-moss">Loading form&hellip;</p>}

        {done && (
          <div data-testid="form-success" className="mt-10 rounded-lg border border-linen bg-ivory p-10 text-center">
            <CircleCheck size={52} className="mx-auto text-forest" />
            <h1 className="mt-5 font-serif text-3xl text-forest">Thank you!</h1>
            <p className="mt-3 text-sm leading-relaxed text-moss">
              Your {definition.title.toLowerCase()} has been received. Nichole will review it before your visit.
            </p>
            <Link to="/" data-testid="form-success-home" className="mt-8 inline-block rounded-md bg-forest px-6 py-3 text-sm font-semibold text-ivory transition-colors hover:bg-forest-dark">
              Back to Home
            </Link>
          </div>
        )}

        {definition && !done && (
          <>
            <h1 data-testid="form-title" className="mt-10 font-serif text-3xl text-forest sm:text-4xl">{definition.title}</h1>
            <p className="mt-3 text-sm leading-relaxed text-moss">{definition.description}</p>

            <form onSubmit={submit} className="mt-8 space-y-5 rounded-lg border border-linen bg-ivory p-6 sm:p-9">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="text-xs font-semibold uppercase tracking-[0.2em] text-moss">Full Name <span className="text-gold-dark">*</span></label>
                  <input id="contact-name" data-testid="form-name-input" type="text" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className="mt-2 w-full rounded-md border border-linen bg-ivory px-4 py-3 text-sm outline-none transition-colors focus:border-gold" />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="text-xs font-semibold uppercase tracking-[0.2em] text-moss">Phone</label>
                  <input id="contact-phone" data-testid="form-phone-input" type="tel" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} className="mt-2 w-full rounded-md border border-linen bg-ivory px-4 py-3 text-sm outline-none transition-colors focus:border-gold" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-email" className="text-xs font-semibold uppercase tracking-[0.2em] text-moss">Email <span className="text-gold-dark">*</span></label>
                  <input id="contact-email" data-testid="form-email-input" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="mt-2 w-full rounded-md border border-linen bg-ivory px-4 py-3 text-sm outline-none transition-colors focus:border-gold" />
                </div>
              </div>

              <div className="space-y-5 border-t border-linen pt-6">
                {definition.fields.map((f) => (
                  <Field key={f.id} field={f} value={answers[f.id]} onChange={(v) => setAnswers({ ...answers, [f.id]: v })} />
                ))}
              </div>

              <div className="space-y-4 border-t border-linen pt-6">
                <div>
                  <label htmlFor="signature" className="text-xs font-semibold uppercase tracking-[0.2em] text-moss">Signature — type your full legal name <span className="text-gold-dark">*</span></label>
                  <input id="signature" data-testid="form-signature-input" type="text" value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Jane Smith" className="mt-2 w-full rounded-md border border-linen bg-ivory px-4 py-3 font-serif text-xl italic text-forest outline-none transition-colors focus:border-gold" />
                </div>
                <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-moss">
                  <input data-testid="form-agree-checkbox" type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-[#1A3626]" />
                  <span>I certify the information provided is accurate and complete, and I understand typing my name above constitutes my electronic signature. <span className="text-gold-dark">*</span></span>
                </label>
              </div>

              <button
                type="submit"
                data-testid="form-submit-button"
                disabled={requiredMissing || busy}
                className="flex w-full items-center justify-center gap-2 rounded-md bg-forest px-6 py-3.5 text-sm font-semibold text-ivory transition-colors duration-300 hover:bg-forest-dark disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Send size={15} /> {busy ? "Submitting..." : "Submit Form"}
              </button>
              <p className="text-center text-xs text-moss/70">Your information is sent securely and only visible to your provider.</p>
            </form>
          </>
        )}
      </div>
    </main>
  );
}
